import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteDialog({ open, onOpenChange }) {
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please enter your name and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Thank you! Your quote request has been sent. We'll get back to you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
      onOpenChange?.(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="sm:max-w-[520px] bg-wood-50 border border-wood-200"
      >
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold-600 font-bold">
            Get a Quote
          </div>
          <DialogTitle className="font-serif text-3xl text-wood-800 mt-1">
            Tell us what you need.
          </DialogTitle>
          <DialogDescription className="text-wood-700/80">
            Share a few details and our team will reach out to you over email.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label htmlFor="q-name" className="text-xs uppercase tracking-widest text-wood-700">
              Your Name
            </Label>
            <Input
              id="q-name"
              data-testid="quote-input-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Rajesh Patel"
              required
              className="bg-white border-wood-200 focus-visible:ring-gold-400"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="q-phone" className="text-xs uppercase tracking-widest text-wood-700">
                Phone (optional)
              </Label>
              <Input
                id="q-phone"
                data-testid="quote-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 9XXXXXXXXX"
                className="bg-white border-wood-200 focus-visible:ring-gold-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-email" className="text-xs uppercase tracking-widest text-wood-700">
                Email (optional)
              </Label>
              <Input
                id="q-email"
                type="email"
                data-testid="quote-input-email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className="bg-white border-wood-200 focus-visible:ring-gold-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="q-message" className="text-xs uppercase tracking-widest text-wood-700">
              Message
            </Label>
            <Textarea
              id="q-message"
              data-testid="quote-input-message"
              value={form.message}
              onChange={update("message")}
              rows={4}
              placeholder="I'm looking for waterproof plywood for a kitchen project…"
              required
              className="bg-white border-wood-200 focus-visible:ring-gold-400"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="btn-primary inline-flex justify-center items-center px-6 py-3 text-sm font-semibold disabled:opacity-60 w-full sm:w-auto"
            >
              {loading ? "Sending…" : "Send Quote Request"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
